// Shared by Cloud Functions and the frontend (imported there as @shared).
// Rivalries and payback games. Everything here uses public information.
//
// Each team has 2-3 rivals in its conference: team.rivals {oppId: {w, l,
// name?}} is the all-time series from this team's side (the same rivalry
// on both teams; some college rivalries have a name). A team is out for
// payback when it lost its last meeting with this opponent this season,
// or, before they've met this season, when that opponent ended its last
// season in the postseason (team.payback {teamId, year}).

/**
 * @param {object} team team document or summary row
 * @param {string} oppId
 * @return {object|null} the rivalry {w, l, name?} from team's side
 */
export function rivalryOf(team, oppId) {
  return team?.rivals?.[oppId] ?? null;
}

/**
 * Visible team strength: the average overall of the 8 best players.
 * @param {object} team team document
 * @return {number}
 */
export function teamStrength(team) {
  const top = Object.values(team?.players ?? {}).map((p) => p.overall)
      .sort((a, b) => b - a).slice(0, 8);
  return top.length ? top.reduce((s, x) => s + x, 0) / top.length : 0;
}

/**
 * Why `teamId` is out for payback against `oppId` before a game on `day`.
 * @param {object} args
 * @param {string} args.teamId
 * @param {string} args.oppId
 * @param {object[]} args.games this season's schedule games ({day, home,
 *   away, result?})
 * @param {number} args.day the game's day (earlier games count)
 * @param {object} [args.payback] team.payback {teamId, year}
 * @param {number} args.year this season
 * @return {object|null} {game} (this season's loss, with its result) or
 *   {lastSeason: true}; null when it isn't a payback game
 */
export function paybackReason({teamId, oppId, games, day, payback, year}) {
  let last = null;
  for (const g of games ?? []) {
    if (!g.result || g.day >= day) continue;
    const met = (g.home === teamId && g.away === oppId) ||
      (g.home === oppId && g.away === teamId);
    if (met && (!last || g.day > last.day)) last = g;
  }
  if (last) {
    const mine = last.home === teamId ? last.result.home : last.result.away;
    const theirs = last.home === teamId ? last.result.away : last.result.home;
    return mine < theirs ? {game: last} : null;
  }
  return payback?.teamId === oppId && payback.year === year - 1 ?
    {lastSeason: true} : null;
}

/**
 * How a side is fired up for a game: the weaker team in a rivalry game
 * (by visible strength) and a team out for payback. The engine turns
 * these into a small lift (TUNING.motivation).
 * @param {object} args
 * @param {object} args.team team document
 * @param {object} args.opp opponent's team document
 * @param {object[]} args.games this season's schedule games
 * @param {number} args.day
 * @param {number} args.year
 * @return {object} {rivalry, underdog, payback}
 */
export function motivation({team, opp, games, day, year}) {
  const rivalry = Boolean(rivalryOf(team, opp.id));
  return {
    rivalry,
    underdog: rivalry && teamStrength(team) < teamStrength(opp),
    payback: Boolean(paybackReason({teamId: team.id, oppId: opp.id, games,
      day, payback: team.payback, year})),
  };
}
